import React from "react";
import { logError } from "@/lib/logger";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
    SquaresFour,
    Briefcase,
    UserCircle,
    SignOut,
    Lightning,
    List,
    X,
    Gift,
    Quotes,
    Rocket,
    Receipt,
    ShieldCheck,
    SealCheck,
    ChalkboardTeacher,
    Star,
    Package,
    ShieldCheckered,
    Heart,
    Bell,
} from "@phosphor-icons/react";
import { InstallAppButton } from "@/components/InstallPrompt";
import PageTransition from "@/components/PageTransition";
import Brand from "@/components/Brand";
import ImpersonationBanner from "@/components/ImpersonationBanner";

const NAV = [
    { to: "/candidate", label: "My applications", icon: SquaresFour, end: true, testid: "cand-nav-apps" },
    { to: "/candidate/jobs", label: "Browse roles", icon: Briefcase, testid: "cand-nav-jobs" },
    // V1.6.9 — Wishlist / saved jobs shortcut sits right under Browse so
    // returning users get a one-tap path to the roles they hearted.
    { to: "/candidate/saved-jobs", label: "Saved jobs", icon: Heart, testid: "cand-nav-saved-jobs" },
    { to: "/candidate/boost", label: "Boost", icon: Rocket, testid: "cand-nav-boost" },
    { to: "/candidate/referrals", label: "Refer & earn", icon: Gift, testid: "cand-nav-referrals" },
    { to: "/candidate/subscriptions", label: "My purchases", icon: Receipt, testid: "cand-nav-subscriptions" },
    { to: "/candidate/verified", label: "Get verified", icon: SealCheck, testid: "cand-nav-verified" },
    { to: "/candidate/interview-prep", label: "Interview prep", icon: ChalkboardTeacher, testid: "cand-nav-interview-prep" },
    { to: "/candidate/featured", label: "Get featured", icon: Star, testid: "cand-nav-featured" },
    { to: "/candidate/bundle", label: "All 4 bundle · Save 41%", icon: Package, testid: "cand-nav-bundle" },
    { to: "/candidate/privacy", label: "Privacy & stats", icon: ShieldCheckered, testid: "cand-nav-privacy" },
    { to: "/candidate/testimonials", label: "Testimonials", icon: Quotes, testid: "cand-nav-testimonials" },
    { to: "/candidate/profile", label: "Profile", icon: UserCircle, testid: "cand-nav-profile" },
    { to: "/candidate/settings/notifications", label: "Notifications", icon: Bell, testid: "cand-nav-notif-prefs" },
    { to: "/candidate/my-data", label: "Privacy & data", icon: ShieldCheck, testid: "cand-nav-my-data" },
];

export default function CandidateLayout() {
    const { user, setUser } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [open, setOpen] = React.useState(false);

    React.useEffect(() => {
        setOpen(false);
    }, [loc.pathname]);

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            logError("Logout failed:", e);
        }
        setUser(false);
        nav("/candidate/login");
    };

    const SidebarContent = (
        <>
            <div className="p-5 lg:p-6 border-b border-[#E5E5E5] flex items-center justify-between">
                <Brand size="h-8" role="candidate" />
                <button
                    className="lg:hidden p-1 -mr-1"
                    onClick={() => setOpen(false)}
                    aria-label="Close menu"
                >
                    <X size={20} weight="bold" />
                </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                {NAV.map((n) => (
                    <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.end}
                        data-testid={n.testid}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 text-sm font-bold transition-colors ${
                                isActive
                                    ? "bg-ink text-white"
                                    : "text-[#525252] hover:bg-[#F5F5F5] hover:text-ink"
                            }`
                        }
                    >
                        <n.icon size={18} weight="bold" />
                        {n.label}
                    </NavLink>
                ))}
            </nav>
            <div className="p-3 border-t border-[#E5E5E5] shrink-0">
                <div className="px-3 py-2">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
                        Signed in
                    </p>
                    <p className="text-sm font-bold text-ink truncate" data-testid="candidate-name">
                        {user?.name}
                    </p>
                    <p className="text-xs text-[#525252] truncate">{user?.email}</p>
                    <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-[#FAF5FF] text-[#7E22CE]">
                        Candidate
                    </span>
                </div>
                <button
                    onClick={logout}
                    data-testid="candidate-logout-button"
                    className="w-full mt-2 flex items-center gap-2 px-3 py-2 text-sm font-bold text-[#525252] hover:text-ink hover:bg-[#F5F5F5] transition-colors"
                >
                    <SignOut size={16} weight="bold" /> Logout
                </button>
                <div className="mt-2">
                    <InstallAppButton
                        label="Install app"
                        testId="candidate-sidebar-install-app"
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-black uppercase tracking-wider border border-cobalt text-cobalt hover:bg-cobalt hover:text-white transition-colors rounded-md"
                    />
                </div>
            </div>
        </>
    );

    return (
        <div className="h-screen flex bg-paper" data-testid="candidate-shell">
            <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-[#E5E5E5] h-topbar-safe safe-top flex items-center justify-between px-4">
                <Brand size="h-7" role="candidate" />
                <button
                    onClick={() => setOpen(true)}
                    data-testid="cand-mobile-menu-button"
                    className="p-2 -mr-2"
                    aria-label="Open menu"
                >
                    <List size={22} weight="bold" />
                </button>
            </div>
            <aside className="hidden lg:flex w-[240px] shrink-0 bg-white border-r border-[#E5E5E5] flex-col sticky top-0 h-screen max-h-screen">
                {SidebarContent}
            </aside>
            {open && (
                <>
                    <div
                        className="lg:hidden fixed inset-0 bg-ink/40 backdrop-blur-sm z-40"
                        onClick={() => setOpen(false)}
                    />
                    <aside className="lg:hidden fixed top-0 bottom-0 left-0 w-[260px] bg-white border-r border-[#E5E5E5] flex flex-col z-50 safe-top">
                        {SidebarContent}
                    </aside>
                </>
            )}
            <main className="flex-1 min-w-0 overflow-x-hidden pt-topbar-safe lg:pt-0 flex flex-col">
                <ImpersonationBanner />
                <PageTransition />
            </main>
        </div>
    );
}
